import type { CSSProperties } from 'react';

import { useReactiveVar } from '@apollo/client';
import { PrimaryButton, hexToRgbArray } from '@movingimage-evp/mi-ui-component-library';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { themePreviewVar } from './theme-preview';
import { AppearanceAssetType, useGetLsproBrandingSettingsQuery } from '../../../generated/graphql-manager';
import { ViewerSourceMode } from '../../../generated/graphql-operator';
import { State } from '../../../generated/graphql-viewer';
import { Content } from '../../../viewer/components/content';
import { ViewerHeader } from '../../../viewer/views/stream/components/header/header';
import { ViewerPlayer } from '../../../viewer/views/stream/components/viewer-player';
import { useCurrentUser } from '../../hooks/current-user';

import styles from './branding.module.css';
import playerStyles from '../../../viewer/views/stream/stream.module.css';

export function BrandingPreview() {
  const { t } = useTranslation();
  const { lsproId } = useCurrentUser();
  const { webcastId = '' } = useParams();
  const preview = useReactiveVar(themePreviewVar);
  const { data } = useGetLsproBrandingSettingsQuery({ variables: { lsproId } });

  const themes = data?.lsproSettings?.themes || [];
  const baseTheme = themes.find((theme) => theme.id === preview?.themeId);
  const defaultTheme = themes.find((theme) => theme.isDefault);
  const themeToDisplay = baseTheme || defaultTheme;

  const webcastRef = useRef({
    id: '',
    lsproId: '',
    accountId: '',
    state: State.LIVE,
    plannedStartAt: new Date().toISOString(),
    durationInMinutes: 60,
    hideWebcastSchedule: false,
    primaryLang: { code: 'de' },
    additionalLangs: [],
    contents: [
      {
        language: 'de',
        title: t('manager.branding.preview.title'),
        preLive: {},
        live: {
          description: JSON.stringify([
            {
              type: 'paragraph',
              children: [{ text: t('manager.branding.preview.description') }],
            },
            {
              type: 'link',
              href: 'https://www.movingimage.com/',
              children: [{ text: 'movingimage.com' }],
            },
          ]),
        },
        postLive: {},
      },
    ],
    securityPolicies: [],
    streaming: {
      live: {
        streams: [
          {
            playback: {
              primaryUrl: window.Cypress
                ? 'http://localhost:3030/hq/live.m3u8'
                : 'https://e.video-cdn.net/video/hls.m3u8?video-id=4Rh-xDMKzd_FRTgtV7gTEY',
              token: '',
            },
            metadata: {
              language: 'de',
            },
            viewerSourceMode: ViewerSourceMode.AUTO,
          },
        ],
      },
    },
    pushedSlides: [],
    termsAndConditions: [],
    authenticationWorkflow: {
      accessGranted: false,
      settings: {},
      metadata: {},
      pathways: [],
    },
  });

  const theme = themeToDisplay
    ? {
        ...themeToDisplay,
        ...(preview?.overrides || {}),
        fonts: {
          ...themeToDisplay.fonts,
          ...(preview?.overrides?.fonts || {}),
        },
        appearanceColors: preview?.overrides?.appearanceColors || themeToDisplay.appearanceColors,
      }
    : undefined;

  if (!theme) return null;

  const appearanceColors = theme.appearanceColors.find((appearanceColors) => appearanceColors.theme === theme.mode);
  const squareLogo = theme.assets.find((asset) => asset.type === AppearanceAssetType.LOGO)?.downloadLink;
  const horizontalLogo = theme.assets.find((asset) => asset.type === AppearanceAssetType.HORIZONTAL_LOGO)?.downloadLink;

  const handleEditTheme = () => {
    themePreviewVar({ themeId: theme.id });
  };

  if (!appearanceColors) return null;

  return (
    <div
      data-testid="branding-preview"
      className={styles.preview}
      data-theme={theme.mode.toLowerCase()}
      style={
        {
          '--primaryColor': hexToRgbArray(appearanceColors.primaryColor).join(','),
          '--backgroundColor': hexToRgbArray(appearanceColors.backgroundColor).join(','),
          '--cardColor': hexToRgbArray(appearanceColors.cardColor).join(','),
          '--headingFontColor': hexToRgbArray(appearanceColors.headingFontColor).join(','),
          '--textFontColor': hexToRgbArray(appearanceColors.textFontColor).join(','),
          '--headingFont': theme.fonts.headingFont,
          '--textFont': theme.fonts.textFont,
        } as CSSProperties
      }
    >
      <div className={styles.themeNameDisplay}>
        {t('manager.branding.preview.themeName', { themeName: theme.name })}

        {preview?.themeId !== theme.id && (
          <PrimaryButton small onClick={handleEditTheme} data-testid="edit-theme-button">
            {t('manager.branding.preview.editThemeButton')}
          </PrimaryButton>
        )}
      </div>

      <ViewerHeader logoUrl={horizontalLogo || squareLogo || ''} />

      <section>
        <div data-testid="branding-video-wrapper" className={playerStyles.videoWrapper}>
          <ViewerPlayer
            className={playerStyles.player}
            accountId={webcastRef.current.accountId}
            lsproId={lsproId}
            webcastId={webcastId}
            title={webcastRef.current.contents[0].title || ''}
            logoUrl={squareLogo || horizontalLogo || ''}
            primaryColor={appearanceColors?.primaryColor}
            iconColor={appearanceColors?.iconColor}
            currentStream={webcastRef.current.streaming.live.streams[0]}
            primaryStreamReady
            viewerSourceMode={webcastRef.current.streaming.live.streams[0].viewerSourceMode}
            muted
          />
        </div>

        <Content language="de" webcast={webcastRef.current} />
      </section>
    </div>
  );
}
